// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { Feedback, NotificationSetting } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUser } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// props
export interface DialogUpdateUserNotificationsProps {
	notifications: NotificationSetting[];
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
}

export const DialogUpdateUserNotifications = ({
	notifications,
	isOpen,
	onClose,
}: DialogUpdateUserNotificationsProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	// handlers
	const handleSubmit = async (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		setFeedback(null);
		setIsLoading(true);

		try {
			const payload = {
				notifications: notifications
					.filter((n) => n.isEnabled)
					.map((n) => ({
						id: n.id,
						project: project?.id,
					})),
			};

			const fetchUpdateUserNotifications = await fetch(
				`${process.env.REACT_APP_API_URL}/users/${user?.id}`,
				{
					method: 'PATCH',
					body: JSON.stringify(payload),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				}
			);

			const resUpdateUserNotifications = await fetchUpdateUserNotifications.json();

			if (resUpdateUserNotifications.error) {
				throw new Error(resUpdateUserNotifications.error.message, {
					cause: {
						id: resUpdateUserNotifications.error.id,
					},
				});
			}

			// set banner
			setBanner({
				hasClose: true,
				message: t('alert.userNotificationsUpdated'),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			console.error(error);

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			setFeedback(feedbackNew);
		} finally {
			// clear loading
			setIsLoading(false);
		}
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear error
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					children: t('saveNotifications'),
					loading: isLoading,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{ color: 'purple', type: 'notification' }}
			isOpen={isOpen}
			maxWidth={500}
			onClose={handleClose}
			title={{
				children: t('saveNotifications'),
			}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography>{t('dialogUpdateUserNotifications.content')}</Typography>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="info">
						{t('alert.updateUserNotifications', {
							email: user?.email,
						})}
					</Alert>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</DialogActions>
	);
};
